import React, { useState, useEffect, useMemo } from 'react'
import { Container, Row, Col, Card, Spinner, Modal, Tabs, Badge } from 'react-bootstrap'
import { ChevronLeft, ChevronRight, X } from 'lucide-react'
import { selectFilterTags, selectSelectedProjectId } from '../../store/slices/projectSlice'
import { useAppSelector } from '../../store/hooks'
import { selectSelectedModelId } from '../../store/slices/modelSlice'
import { useGetModelQuery } from '../../services/modelsApi'
import { useFetchAnnotatedContentQuery, useGetContentImageQuery } from '../../services/contentApi'
import styled from 'styled-components'
import { AnnotationDataBoundingBox } from '../../types/models/annotation'
import ModelDatasetViewHeader from './ModelDatasetView/ModelDatasetViewHeader'
import ContentImage from '../tools/ContentImage'
import { useDatasetStats } from '../../utils/datasetStats'
import { StyledCard } from '../common/styled'

const NavigationButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(0, 0, 0, 0.5);
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  cursor: pointer;
  transition: all 0.2s ease;
  z-index: 1000;

  &:hover {
    background: rgba(0, 0, 0, 0.7);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &.left {
    left: 20px;
  }

  &.right {
    right: 20px;
  }
`

const CloseButton = styled(NavigationButton)`
  top: 20px;
  right: 20px;
  transform: none;
  z-index: 1001;
`

const ModalContainer = styled(Modal)`
  .modal-dialog {
    max-width: 70vw;
    margin: 1.75rem auto;
  }

  .modal-content {
    background-color: #1e1e1e;
    height: 70vh;
  }

  .modal-body {
    height: 100%;
    padding: 0;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
  }
`

const ImageContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #1e1e1e;

  img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }

  /* Disable hover effects */
  * {
    transform: none !important;
    transition: none !important;
    cursor: default !important;
  }
`

const GalleryContainer = styled.div`
  height: calc(78vh - 64px); /* Adjust this value based on your layout's top spacing */
  min-height: 0;
  background: #1a1a1a;
  border-radius: 8px;
  position: relative;
  display: flex;
  flex-direction: column;
`

const HeaderContainer = styled.div`
  position: sticky;
  top: 0;
  z-index: 100;
  background: #1a1a1a;
  padding: 1rem 1rem 0 1rem;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
`

const ScrollContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 1rem;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #1a1a1a;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: #4a4a4a;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #5a5a5a;
  }
`

const GridContainer = styled.div<{ $imagesPerRow: number }>`
  display: grid;
  grid-template-columns: repeat(${(props) => props.$imagesPerRow}, minmax(0, 1fr));
  gap: 1rem;
  width: 100%;
`

const ImageGridItem = styled.div`
  aspect-ratio: 1;
  width: 100%;
  min-height: 200px;
  background: #2a2a2a;
  border-radius: 4px;
  overflow: hidden;
  cursor: pointer;
  transition: transform 0.2s ease-in-out;

  &:hover {
    transform: scale(1.02);
  }
`

const StyledTabs = styled(Tabs)`
  border-bottom: none;
  margin-bottom: 1.5rem;

  .nav-item {
    margin-right: 0.5rem;
  }

  .nav-link {
    background: #2a2a2a;
    border: none;
    color: #a0a0a0;
    padding: 0.75rem 1.25rem;
    border-radius: 8px;
    transition: all 0.2s ease;

    &:hover {
      background: #3a3a3a;
      color: #fff;
    }

    &.active {
      background: #4a4a4a;
      color: #fff;
      font-weight: 500;
    }
  }
`

const SummaryCard = styled(StyledCard)`
  padding: 0.75rem;

  .card-body {
    padding: 1rem;
  }

  .card-title {
    font-size: 1rem;
    margin-bottom: 0.75rem;
  }

  h6 {
    font-size: 0.9rem;
    margin-bottom: 0.5rem;
  }

  p {
    font-size: 0.85rem;
    margin-bottom: 0.5rem;
  }
`

const StatGroup = styled.div`
  margin-bottom: 1rem;

  &:last-child {
    margin-bottom: 0;
  }
`

const StatLabel = styled.span`
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.7);
  display: block;
`

const StatValue = styled.span`
  font-size: 0.9rem;
  font-weight: 600;
  color: white;
`

const SourcesContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.5rem;
  margin-bottom: 0.75rem;
`

interface ModelDatasetViewProps {
  onImageSelect?: (imageId: string) => void
}

const noop = () => undefined

const ModelDatasetView: React.FC<ModelDatasetViewProps> = ({ onImageSelect }): JSX.Element => {
  const selectedProjectId = useAppSelector(selectSelectedProjectId)
  const selectedModelId = useAppSelector(selectSelectedModelId)
  const { data: modelData } = useGetModelQuery(selectedModelId || '', {
    skip: !selectedModelId,
  })
  const datasetId = modelData?.datasetid

  const [showModal, setShowModal] = useState(false)
  const [selectedImageIndex, setSelectedImageIndex] = useState<number>(0)
  const [convertedBase64, setConvertedBase64] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<'training' | 'validation'>('training')
  const [trainingPage, setTrainingPage] = useState(1)
  const [validationPage, setValidationPage] = useState(1)
  const [imagesPerRow, setImagesPerRow] = useState(4)
  const itemsPerPage = 100

  const selectedFilterTags = useAppSelector(selectFilterTags)

  // Get the appropriate datasource array based on the active tab
  const datasourceArray = useMemo(() => {
    if (!modelData?.parameters) return []
    return activeTab === 'training'
      ? modelData.parameters.train_metadata?.datasource || []
      : modelData.parameters.validation_metadata?.datasource || []
  }, [modelData?.parameters, activeTab])

  const currentPage = useMemo(
    () => (activeTab === 'training' ? trainingPage : validationPage),
    [activeTab, trainingPage, validationPage]
  )

  const datasetStatsMap = useDatasetStats(selectedProjectId || '', modelData?.datasetid || '', datasourceArray)

  // Update the content query to use the correct datasource based on active tab
  const {
    data: contentData,
    isLoading,
    isFetching,
    error,
  } = useFetchAnnotatedContentQuery(
    {
      project_id: selectedProjectId || '',
      dataset_id: modelData?.datasetid || '',
      page: currentPage - 1,
      limit: itemsPerPage,
      metadata_filter: datasourceArray.length > 0 ? { datasource: datasourceArray } : undefined,
      operator: 'and',
      sort_key: 'updated_at',
      sort_val: -1,
      // tag_id: selectedFilterTags?.length > 0 ? selectedFilterTags.map((t) => t.id) : undefined,
    },
    {
      skip: !selectedProjectId || !modelData?.datasetid || datasourceArray.length === 0,
      refetchOnMountOrArgChange: true,
    }
  )

  // Also update the stats card display
  const getSourceImagesTotal = (sources: string[] | undefined) => {
    if (!sources) return 0
    return sources.reduce((total, source) => {
      const s = datasetStatsMap[source]
      if (!s) return total
      const images = (s.total_annotated_images || 0) + (s.total_unannotated_images || 0)
      return total + images
    }, 0)
  }

  const convertBlobToBase64 = async (blob: Blob): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onloadend = () => {
        const base64String = reader.result as string
        // Remove the data URL prefix (e.g., "data:image/jpeg;base64,")
        const base64Data = base64String.split(',')[1]
        resolve(base64Data)
      }
      reader.onerror = reject
      reader.readAsDataURL(blob)
    })
  }

  // Add logging to debug
  useEffect(() => {
    console.log('Tab changed:', activeTab)
    console.log('Datasource array:', datasourceArray)
    console.log('Content data:', contentData)
  }, [activeTab, datasourceArray, contentData])

  const handlePageChange = (page: number) => {
    if (activeTab === 'training') {
      setTrainingPage(page)
    } else {
      setValidationPage(page)
    }
  }

  const handleTabChange = (tab: 'training' | 'validation') => {
    setActiveTab(tab)
  }

  // Add cache-busting state for image refreshing
  const [imageCacheTimestamp, setImageCacheTimestamp] = React.useState<number>(Date.now())

  // Listen for annotation updates to refresh images
  React.useEffect(() => {
    const handleAnnotationUpdate = () => {
      setImageCacheTimestamp(Date.now())
    }

    window.addEventListener('annotation-updated', handleAnnotationUpdate)
    window.addEventListener('reset-image-loading', handleAnnotationUpdate)

    return () => {
      window.removeEventListener('annotation-updated', handleAnnotationUpdate)
      window.removeEventListener('reset-image-loading', handleAnnotationUpdate)
    }
  }, [])

  // Fetch the selected image data when modal is open
  const { data: selectedImageData } = useGetContentImageQuery(
    {
      ContentID: contentData?.content[selectedImageIndex]?.id || '',
      project_id: selectedProjectId || '',
      dataset_id: modelData?.datasetid || '',
      _t: imageCacheTimestamp,
    },
    {
      skip: !showModal || !contentData?.content[selectedImageIndex],
    }
  )

  useEffect(() => {
    if (!selectedImageData || !showModal) return

    const loadImage = async () => {
      try {
        const response = await fetch(selectedImageData)
        const blob = await response.blob()
        const reader = new FileReader()
        reader.onloadend = () => {
          const base64String = reader.result as string
          const base64Data = base64String.split(',')[1]
          setConvertedBase64(base64Data)
        }
        reader.readAsDataURL(blob)
      } catch (error) {
        console.error('Error loading image:', error)
      }
    }

    loadImage()
  }, [selectedImageData, showModal])

  const handleImageClick = (index: number) => {
    setSelectedImageIndex(index)
    setShowModal(true)
    onImageSelect?.(contentData?.content[index].id || '')
  }

  const handlePrevImage = () => {
    setSelectedImageIndex((prev) => (prev > 0 ? prev - 1 : prev))
    setConvertedBase64(null)
  }

  const handleNextImage = () => {
    setSelectedImageIndex((prev) =>
      prev < (contentData?.content.length || 0) - 1 ? prev + 1 : prev
    )
    setConvertedBase64(null)
  }

  // Transform bounding boxes to include required fields
  const getBoundingBoxes = (content: any): AnnotationDataBoundingBox[] => {
    if (!content.annotation || !Array.isArray(content.annotation)) {
      return []
    }

    const boxes = content.annotation.reduce(
      (boxes: AnnotationDataBoundingBox[], annotation: any) => {
        if (annotation.metadata?.bounding_boxes) {
          return [
            ...boxes,
            ...annotation.metadata.bounding_boxes.map((box: any, index: number) => {
              // Normalize coordinates to the original image dimensions
              const normalizedBox = {
                ...box,
                xmin: Math.max(0, Math.min(content.width, box.xmin)),
                xmax: Math.max(0, Math.min(content.width, box.xmax)),
                ymin: Math.max(0, Math.min(content.height, box.ymin)),
                ymax: Math.max(0, Math.min(content.height, box.ymax)),
                class_index: index,
                class_name: 'Object',
                confidence: 1.0,
              }
              return normalizedBox
            }),
          ]
        }
        return boxes
      },
      []
    )

    return boxes
  }

  if (isLoading || isFetching) {
    return (
      <Container
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: '200px' }}
      >
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </Container>
    )
  }

  if (error) {
    return (
      <Container>
        <div className="alert alert-danger" role="alert">
          An error occurred while loading the dataset images.
        </div>
      </Container>
    )
  }

  return (
    <>
      <Container fluid>
        <Row>
          <Col md={4} className="mt-4">
            <StyledCard>
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <Card.Title className="text-white mb-0">{modelData?.name || 'Model'}</Card.Title>
                </div>

                <div className="mb-4">
                  <h6 className="text-white">Training Dataset</h6>
                  <Row>
                    <Col xs={6}>
                      <div className="text-muted small">Total Images</div>
                      <div className="text-white fw-bold">{getSourceImagesTotal(modelData?.parameters?.train_metadata?.datasource)}</div>
                    </Col>
                    <Col xs={6}>
                      <div className="text-muted small">Sources</div>
                      <div className="d-flex flex-wrap gap-1 mt-1">
                        {modelData?.parameters?.train_metadata?.datasource?.map((source, index) => (
                          <Badge
                            key={index}
                            bg="info"
                            className="me-1"
                            style={{ fontSize: '0.75rem' }}
                          >
                            {source}
                          </Badge>
                        ))}
                      </div>
                    </Col>
                  </Row>
                </div>

                <div>
                  <h6 className="text-white">Validation Dataset</h6>
                  <Row>
                    <Col xs={6}>
                      <div className="text-muted small">Total Images</div>
                      <div className="text-white fw-bold">{getSourceImagesTotal(modelData?.parameters?.validation_metadata?.datasource)}</div>
                    </Col>
                    <Col xs={6}>
                      <div className="text-muted small">Sources</div>
                      <div className="d-flex flex-wrap gap-1 mt-1">
                        {modelData?.parameters?.validation_metadata?.datasource?.map(
                          (source, index) => (
                            <Badge
                              key={index}
                              bg="info"
                              className="me-1"
                              style={{ fontSize: '0.75rem' }}
                            >
                              {source}
                            </Badge>
                          )
                        )}
                      </div>
                    </Col>
                  </Row>
                </div>
              </Card.Body>
            </StyledCard>
          </Col>
          <Col>
            <GalleryContainer>
              <HeaderContainer>
                <ModelDatasetViewHeader
                  datasetId={datasetId || ''}
                  imagesPerRow={imagesPerRow}
                  onImagesPerRowChange={setImagesPerRow}
                  currentPage={currentPage}
                  totalImages={getSourceImagesTotal(datasourceArray)}
                  itemsPerPage={itemsPerPage}
                  onPageChange={handlePageChange}
                  activeTab={activeTab}
                  onTabChange={handleTabChange}
                />
              </HeaderContainer>
              {isLoading || isFetching ? (
                <div className="d-flex justify-content-center align-items-center h-100">
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </Spinner>
                </div>
              ) : contentData?.content && contentData.content.length > 0 ? (
                <ScrollContainer>
                  <GridContainer $imagesPerRow={imagesPerRow}>
                    {contentData.content.map((image, index) => (
                      <ImageGridItem key={image.id} onClick={() => handleImageClick(index)}>
                        <ContentImage
                          content={image}
                          onImageClick={() => handleImageClick(index)}
                          selectable={false}
                        />
                      </ImageGridItem>
                    ))}
                  </GridContainer>
                </ScrollContainer>
              ) : (
                <div className="d-flex justify-content-center align-items-center h-100">
                  <p className="text-muted">No images available</p>
                </div>
              )}
            </GalleryContainer>
          </Col>
        </Row>
      </Container>

      <ModalContainer show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Body>
          <CloseButton onClick={() => setShowModal(false)}>
            <X size={24} />
          </CloseButton>

          <NavigationButton
            className="left"
            onClick={handlePrevImage}
            disabled={selectedImageIndex === 0}
          >
            <ChevronLeft size={24} />
          </NavigationButton>

          <NavigationButton
            className="right"
            onClick={handleNextImage}
            disabled={selectedImageIndex === (contentData?.content?.length || 0) - 1}
          >
            <ChevronRight size={24} />
          </NavigationButton>

          <ImageContainer>
            {contentData?.content?.[selectedImageIndex] && convertedBase64 ? (
              <div style={{ width: '100%', height: '100%', pointerEvents: 'none' }}>
                <ContentImage
                  content={{
                    ...contentData.content[selectedImageIndex],
                    b64_image: convertedBase64,
                  }}
                  onImageClick={noop}
                  selectable={false}
                />
              </div>
            ) : (
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
            )}
          </ImageContainer>
        </Modal.Body>
      </ModalContainer>
    </>
  )
}

export default ModelDatasetView
